import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { notifications } from '@mantine/notifications';
import type { ITaskStore } from '../stores/ITaskStore';
import type { TaskSnapshot } from '../stores/types';
import {
  computeDurationFromStartAndFinish,
  computeFinishFromStartAndDuration,
} from '../gantt/addTaskUtils';
import {
  applyPredecessorInputToStore,
  buildPredecessorDisplayText,
  buildPredecessorFieldLabel,
  normalizePredecessorDraftInput,
  predecessorDraftInputsEquivalent,
  type PredecessorLinkChange,
} from '../gantt/taskLinkDisplayUtils';
import {
  formatDateForBackend,
  mapPlanningApiTaskRowToSnapshot,
  parseDateSafe,
  planningFetchTaskRowId,
  unwrapPlanningFetchTaskResponse,
} from '../utils/utils';
import { fetchTask, updateTaskQuantity } from '../api/schedulingApis';
import {
  buildQuantitySave,
  isConvertedTask,
  isRejectedUomChange,
  quantityDraftIsDirty,
  type QuantityField,
} from '../gantt/taskQuantity';
import { deferAfterPaint } from '../utils/deferAfterPaint';
import { useTaskDetailsSelection } from './useTaskDetailsSelection';

/** Fields the user can edit in task details (draft-only until Save). */
export const TASK_DETAILS_EDITABLE_FIELDS = [
  'taskName',
  'start',
  'finish',
  'duration',
  'percentComplete',
  'notes',
] as const;

export type TaskDetailsEditableField = (typeof TASK_DETAILS_EDITABLE_FIELDS)[number];

function cloneTaskSnapshot(task: TaskSnapshot): TaskSnapshot {
  return { ...task };
}

function valuesEqual(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  if (a == null && b == null) return true;
  if (typeof a === 'number' && typeof b === 'number') {
    return Number.isFinite(a) && Number.isFinite(b) && a === b;
  }
  return String(a ?? '') === String(b ?? '');
}

export function computeTaskDetailsDraftChanges(
  baseline: TaskSnapshot,
  draft: TaskSnapshot
): Partial<TaskSnapshot> {
  const changes: Partial<TaskSnapshot> = {};
  for (const key of TASK_DETAILS_EDITABLE_FIELDS) {
    if (!valuesEqual(draft[key], baseline[key])) {
      (changes as Record<string, unknown>)[key] = draft[key];
    }
  }
  return changes;
}

function applyScheduleFieldChange(
  draft: TaskSnapshot,
  field: TaskDetailsEditableField,
  value: unknown,
  syncSchedule: boolean
): TaskSnapshot {
  const next = { ...draft, [field]: value } as TaskSnapshot;
  if (!syncSchedule) {
    return next;
  }

  const startDate = parseDateSafe(next.start ?? null);
  if (!startDate) {
    return next;
  }

  if (field === 'start' || field === 'duration') {
    const duration = Math.max(0, Number(next.duration) || 0);
    const finish = computeFinishFromStartAndDuration(startDate, duration);
    return {
      ...next,
      duration,
      finish: formatDateForBackend(finish),
    };
  }

  if (field === 'finish') {
    const finishDate = parseDateSafe(next.finish ?? null);
    if (!finishDate) {
      return next;
    }
    return {
      ...next,
      duration: computeDurationFromStartAndFinish(startDate, finishDate),
    };
  }

  return next;
}

export interface UseTaskDetailsDraftOptions {
  /** Applied to store + Gantt once on Save (not on every keystroke). */
  onApplyChanges?: (taskId: string, changes: Partial<TaskSnapshot>) => void;
  /** Called after predecessor links are synced on Save (session + gantt scheduling). */
  onCommitPredecessorLinks?: (taskId: string, change: PredecessorLinkChange) => void;
  /** dhtmlx WBS lookup — needed to resolve WBS codes typed in the predecessor field. */
  resolvePredecessorWbsCode?: (taskId: string) => string | null;
  /** Fired after a successful Save (local draft applied). */
  onSaveSuccess?: (taskId: string) => void;
}

export interface UseTaskDetailsDraftResult {
  selectedTaskId: string | null;
  /** Store-backed task (read-only meta, tracking, links). */
  storeTask: TaskSnapshot | null;
  /** Local editable copy; does not touch store until Save. */
  draft: TaskSnapshot | null;
  isDirty: boolean;
  isSaving: boolean;
  isLeafTask: boolean;
  readOnlyForNonLeaf: boolean;
  resolvedLocationValue: string;
  draftPredecessors: string;
  successorText: string;
  predecessorFieldLabel: string;
  successorFieldLabel: string;
  updateDraftField: (field: TaskDetailsEditableField, value: unknown) => void;
  updateDraftQuantity: (field: QuantityField, value: string | number | null) => void;
  quantityErrors: Partial<Record<QuantityField, string>>;
  quantityDirty: boolean;
  updateDraftPredecessors: (value: string) => void;
  commitDraftPredecessors: (value?: string) => void;
  saveChanges: () => void;
  cancelChanges: () => void;
}

export function useTaskDetailsDraft(
  store: ITaskStore,
  options?: UseTaskDetailsDraftOptions
): UseTaskDetailsDraftResult {
  const { onApplyChanges, onCommitPredecessorLinks, resolvePredecessorWbsCode, onSaveSuccess } =
    options ?? {};
  const [draft, setDraft] = useState<TaskSnapshot | null>(null);
  const [baseline, setBaseline] = useState<TaskSnapshot | null>(null);
  const [draftPredecessors, setDraftPredecessors] = useState('');
  const [baselinePredecessors, setBaselinePredecessors] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [quantityErrors, setQuantityErrors] = useState<Partial<Record<QuantityField, string>>>({});
  const boundTaskIdRef = useRef<string | null>(null);
  const lastSyncedTaskRevisionRef = useRef(-1);
  const lastSyncedLinksRevisionRef = useRef(-1);

  const tasksRevision = store.getTasksRevision();
  const linksRevision = store.getLinksRevision();

  const quantityContext = useMemo(() => {
    const isLeaf = draft ? !store.taskHasChildren(draft.id) : true;
    return { isLeaf, converted: isConvertedTask(draft?.unitActivityDetails) };
  }, [draft, store, tasksRevision]);

  const quantityDirty = useMemo(() => {
    if (!draft || !baseline) return false;
    return quantityDraftIsDirty(baseline, draft, quantityContext);
  }, [draft, baseline, quantityContext]);

  const isDirty = useMemo(() => {
    if (!draft || !baseline) return false;
    const taskDirty = Object.keys(computeTaskDetailsDraftChanges(baseline, draft)).length > 0;
    const predecessorDirty = !predecessorDraftInputsEquivalent(
      draftPredecessors,
      baselinePredecessors,
      store.getTaskValues(),
      resolvePredecessorWbsCode,
    );
    return taskDirty || predecessorDirty || quantityDirty;
  }, [draft, baseline, draftPredecessors, baselinePredecessors, store, resolvePredecessorWbsCode, quantityDirty]);

  const selection = useTaskDetailsSelection(store, { skipSyncWhileUpdating: isDirty || isSaving });
  const storeTask = selection.task;

  const predecessorFieldLabel = useMemo(() => {
    if (!draft) return 'Predecessor';
    return buildPredecessorFieldLabel(draftPredecessors, store.getTaskValues(), resolvePredecessorWbsCode);
  }, [draft, draftPredecessors, store, linksRevision, resolvePredecessorWbsCode]);

  const resetDraftFromStore = useCallback(
    (task: TaskSnapshot, predecessorsOverride?: string) => {
      const copy = cloneTaskSnapshot(task);
      const fromLinks = buildPredecessorDisplayText(
        task.id,
        store.getLinks(),
        (id) => store.getTask(id) ?? null,
      );
      const predecessors = fromLinks || predecessorsOverride || '';
      setBaseline(copy);
      setDraft(cloneTaskSnapshot(task));
      setBaselinePredecessors(predecessors);
      setDraftPredecessors(predecessors);
      setQuantityErrors({});
      boundTaskIdRef.current = task.id;
      lastSyncedTaskRevisionRef.current = store.getTasksRevision();
      lastSyncedLinksRevisionRef.current = store.getLinksRevision();
    },
    [store]
  );

  useEffect(() => {
    if (!storeTask) {
      setDraft(null);
      setBaseline(null);
      setDraftPredecessors('');
      setBaselinePredecessors('');
      setQuantityErrors({});
      boundTaskIdRef.current = null;
      lastSyncedTaskRevisionRef.current = -1;
      lastSyncedLinksRevisionRef.current = -1;
      return;
    }

    if (boundTaskIdRef.current !== storeTask.id) {
      resetDraftFromStore(storeTask);
      return;
    }

    if (isDirty) return;

    const taskRev = store.getTasksRevision();
    const linkRev = store.getLinksRevision();
    if (
      taskRev !== lastSyncedTaskRevisionRef.current ||
      linkRev !== lastSyncedLinksRevisionRef.current
    ) {
      resetDraftFromStore(storeTask);
    }
  }, [storeTask, isDirty, tasksRevision, linksRevision, resetDraftFromStore, store]);

  const updateDraftField = useCallback(
    (field: TaskDetailsEditableField, value: unknown) => {
      setDraft((prev) => {
        if (!prev) return prev;
        const syncSchedule =
          !selection.readOnlyForNonLeaf &&
          (field === 'start' || field === 'finish' || field === 'duration');
        return applyScheduleFieldChange(prev, field, value, syncSchedule);
      });
    },
    [selection.readOnlyForNonLeaf]
  );

  const updateDraftQuantity = useCallback((field: QuantityField, value: string | number | null) => {
    setDraft((prev) => {
      if (!prev) return prev;
      if (field === 'uom') {
        const text = value == null ? '' : String(value);
        return { ...prev, uom: text === '' ? null : text };
      }
      if (field === 'totalQuantity' || field === 'achievedQuantity') {
        if (value == null || value === '') return { ...prev, [field]: null };
        const n = typeof value === 'number' ? value : Number(value);
        return { ...prev, [field]: Number.isFinite(n) ? n : null };
      }
      return prev;
    });
    setQuantityErrors((prev) => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
  }, []);

  const updateDraftPredecessors = useCallback((value: string) => {
    if (selection.readOnlyForNonLeaf) return;
    setDraftPredecessors(value);
  }, [selection.readOnlyForNonLeaf]);

  const commitDraftPredecessors = useCallback(
    (value?: string) => {
      if (selection.readOnlyForNonLeaf) return;
      const raw = value ?? draftPredecessors;
      const normalized = normalizePredecessorDraftInput(
        raw,
        store.getTaskValues(),
        resolvePredecessorWbsCode,
      );
      if (normalized !== draftPredecessors) {
        setDraftPredecessors(normalized);
      }
    },
    [draftPredecessors, store, resolvePredecessorWbsCode, selection.readOnlyForNonLeaf],
  );

  const cancelChanges = useCallback(() => {
    if (baseline) {
      setDraft(cloneTaskSnapshot(baseline));
      setDraftPredecessors(baselinePredecessors);
      setQuantityErrors({});
    } else if (storeTask) {
      resetDraftFromStore(storeTask);
    }
  }, [baseline, baselinePredecessors, storeTask, resetDraftFromStore]);

  const saveChanges = useCallback(() => {
    if (!draft || !baseline || isSaving) return;

    const quantityPlan = buildQuantitySave(baseline, draft, quantityContext);
    if (Object.keys(quantityPlan.errors).length > 0) {
      setQuantityErrors(quantityPlan.errors);
      return;
    }
    setQuantityErrors({});

    const changes = computeTaskDetailsDraftChanges(baseline, draft);
    if (quantityPlan.changed && draft.totalQuantity != null && draft.totalQuantity > 0) {
      delete changes.percentComplete;
    }
    const tasks = store.getTaskValues();
    const canEditPredecessors = !selection.readOnlyForNonLeaf;
    const committedPredecessors = canEditPredecessors
      ? normalizePredecessorDraftInput(
          draftPredecessors,
          tasks,
          resolvePredecessorWbsCode,
        )
      : baselinePredecessors;

    const predecessorsChanged =
      canEditPredecessors &&
      !predecessorDraftInputsEquivalent(
        committedPredecessors,
        baselinePredecessors,
        tasks,
        resolvePredecessorWbsCode,
      );

    if (Object.keys(changes).length === 0 && !predecessorsChanged && !quantityPlan.changed) return;

    const taskId = draft.id;
    const projectId = draft.projectId;
    const savedUom = baseline.uom ?? null;

    setIsSaving(true);
    deferAfterPaint(() => {
      void (async () => {
        let didSave = false;
        try {
          if (quantityPlan.changed) {
            try {
              await updateTaskQuantity(taskId, { projectId, ...quantityPlan.body });
            } catch (err) {
              if (isRejectedUomChange(err)) {
                setDraft((prev) => (prev && prev.id === taskId ? { ...prev, uom: savedUom } : prev));
              }
              return;
            }

            try {
              const raw = await fetchTask({ projectId, taskId });
              const row = unwrapPlanningFetchTaskResponse(raw, taskId);
              if (!row || planningFetchTaskRowId(row) !== String(taskId)) {
                throw new Error('fetchTask: missing or mismatched task in response');
              }
              const refreshed = mapPlanningApiTaskRowToSnapshot(row);
              const patch: Partial<TaskSnapshot> = {
                uom: refreshed.uom ?? null,
                totalQuantity: refreshed.totalQuantity ?? null,
                achievedQuantity: refreshed.achievedQuantity ?? null,
                percentComplete: refreshed.percentComplete,
              };
              store.updateTask(taskId, patch);
              setBaseline((prev) => (prev && prev.id === taskId ? { ...prev, ...patch } : prev));
              setDraft((prev) => (prev && prev.id === taskId ? { ...prev, ...patch } : prev));
              didSave = true;
            } catch {
              return;
            }
          }

          // Apply predecessor links before task-field gantt updates so row refresh sees new links.
          if (predecessorsChanged) {
            try {
              const change = applyPredecessorInputToStore({
                taskId,
                input: committedPredecessors,
                tasks,
                links: store.getLinks(),
                resolveWbsCode: resolvePredecessorWbsCode,
                addLink: (link) => store.addLink(link),
                removeLink: (id) => store.removeLink(id),
              });
              onCommitPredecessorLinks?.(taskId, change);
              didSave = true;
            } catch (err: unknown) {
              const message =
                err instanceof Error
                  ? err.message
                  : 'That predecessor is not allowed.';
              notifications.show({
                title: 'Invalid predecessor',
                message,
                color: 'red',
              });
              return;
            }
          }

          if (Object.keys(changes).length > 0) {
            store.updateTask(taskId, changes);
            onApplyChanges?.(taskId, changes);
            didSave = true;
          }

          const updated = store.getTask(taskId);
          if (updated) {
            resetDraftFromStore(
              updated,
              predecessorsChanged ? committedPredecessors : undefined,
            );
          }

          if (didSave) {
            onSaveSuccess?.(taskId);
          }
        } finally {
          setIsSaving(false);
        }
      })();
    });
  }, [
    draft,
    baseline,
    isSaving,
    quantityContext,
    draftPredecessors,
    baselinePredecessors,
    store,
    onApplyChanges,
    onCommitPredecessorLinks,
    onSaveSuccess,
    resolvePredecessorWbsCode,
    resetDraftFromStore,
    selection.readOnlyForNonLeaf,
  ]);

  return {
    selectedTaskId: selection.selectedTaskId,
    storeTask,
    draft,
    isDirty,
    isSaving,
    isLeafTask: selection.isLeafTask,
    readOnlyForNonLeaf: selection.readOnlyForNonLeaf,
    resolvedLocationValue: selection.resolvedLocationValue,
    draftPredecessors,
    successorText: selection.successorText,
    predecessorFieldLabel,
    successorFieldLabel: selection.successorFieldLabel,
    updateDraftField,
    updateDraftQuantity,
    quantityErrors,
    quantityDirty,
    updateDraftPredecessors,
    commitDraftPredecessors,
    saveChanges,
    cancelChanges,
  };
}
